import React, { useRef, useMemo, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars } from '@react-three/drei';
import * as THREE from 'three';

const useParticlePhysics = (count, settings) => {
  const particles = useMemo(() => {
    const data = [];
    for (let i = 0; i < count; i++) {
      const isCenter = i === 0 && settings.massiveCenter;
      data.push({
        pos: isCenter ? new THREE.Vector3(0, 0, 0) : new THREE.Vector3((Math.random() - 0.5) * settings.boxSize, (Math.random() - 0.5) * settings.boxSize, (Math.random() - 0.5) * settings.boxSize),
        vel: isCenter ? new THREE.Vector3(0, 0, 0) : new THREE.Vector3((Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3),
        frc: new THREE.Vector3(0, 0, 0),
        mass: isCenter ? settings.centerMass : Math.random() * 150 + 20,
        pointSize: isCenter ? settings.particleSize * 2 : settings.particleSize,
        memPos: [],
        isCenter: isCenter
      });
    }
    return data;
  }, [count, settings.boxSize, settings.massiveCenter, settings.centerMass, settings.particleSize]);

  useFrame(() => {
    particles.forEach(p => p.frc.set(0, 0, 0));

    for (let i = 0; i < particles.length; i++) {
      for (let j = i + 1; j < particles.length; j++) {
        const p1 = particles[i];
        const p2 = particles[j];
        const diff = new THREE.Vector3().subVectors(p1.pos, p2.pos);
        const dist = diff.length();
        if (dist < 5000 && dist > 0.1) {
          const pct = 1 - (dist / 5000);
          const force = diff.normalize().multiplyScalar(pct * settings.speedRatio);
          p1.frc.sub(force);
          p2.frc.add(force);
        }
      }
    }

    particles.forEach(p => {
      // If it's the center, we keep it at 0,0,0 (like your ofApp logic)
      if (p.isCenter) {
        p.pos.set(0, 0, 0);
      } else {
        p.vel.add(p.frc.clone().divideScalar(p.mass));
        p.pos.add(p.vel);
      }
      
      p.memPos.push(p.pos.clone());
      if (p.memPos.length > settings.memorySize) p.memPos.shift();
    });
  });

  return particles;
};

const ParticleSystem = ({ settings }) => {
  const mainMeshRef = useRef();
  const trailMeshRef = useRef();
  const lineRef = useRef();
  const particles = useParticlePhysics(settings.amount, settings);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  // Set the colors once on mount
  useEffect(() => {
    if (mainMeshRef.current) {
      const color = new THREE.Color();
      for (let i = 0; i < settings.amount; i++) {
        // Center is Red, others are White
        color.set(i === 0 && settings.massiveCenter ? '#ff0000' : '#ffffff');
        mainMeshRef.current.setColorAt(i, color);
      }
      mainMeshRef.current.instanceColor.needsUpdate = true;
    }
  }, [settings.amount, settings.massiveCenter]);

  useFrame(() => {
    if (!mainMeshRef.current || !trailMeshRef.current || !lineRef.current) return;

    let linePos = [];
    let lineCol = [];
    let trailIdx = 0;

    // First: Reset all trail scales to 0 so "unused" trail slots don't cluster in the center
    for (let t = 0; t < settings.amount * settings.memorySize; t++) {
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        trailMeshRef.current.setMatrixAt(t, dummy.matrix);
    }

    particles.forEach((p, i) => {
      // 1. Update Main Particle Position
      dummy.position.copy(p.pos);
      dummy.scale.setScalar(p.isCenter ? 1.5 : 1);
      dummy.updateMatrix();
      mainMeshRef.current.setMatrixAt(i, dummy.matrix);

      // 2. Update Lines
      for (let j = i + 1; j < particles.length; j++) {
        const p2 = particles[j];
        for (let k = 0; k < p.memPos.length; k++) {
          if (p2.memPos[k]) {
            const d = p.memPos[k].distanceTo(p2.memPos[k]);
            if (d < settings.memoryLineDistance) {
              linePos.push(p.memPos[k].x, p.memPos[k].y, p.memPos[k].z);
              linePos.push(p2.memPos[k].x, p2.memPos[k].y, p2.memPos[k].z);
              const alpha = (k / settings.memorySize) * 0.4;
              lineCol.push(1, 1, 1, alpha, 1, 1, 1, alpha);
            }
          }
        }
      }
    });

    mainMeshRef.current.instanceMatrix.needsUpdate = true;
    trailMeshRef.current.instanceMatrix.needsUpdate = true;
    
    lineRef.current.geometry.setAttribute('position', new THREE.Float32BufferAttribute(linePos, 3));
    lineRef.current.geometry.setAttribute('color', new THREE.Float32BufferAttribute(lineCol, 4));
    lineRef.current.geometry.attributes.position.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={mainMeshRef} args={[null, null, settings.amount]}>
        <sphereGeometry args={[settings.particleSize, 12, 12]} />
        <meshStandardMaterial vertexColors={false} /> 
      </instancedMesh>

      <instancedMesh ref={trailMeshRef} args={[null, null, settings.amount * settings.memorySize]}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshBasicMaterial color="white" transparent opacity={0.2} />
      </instancedMesh>

      <lineSegments ref={lineRef}>
        <bufferGeometry />
        <lineBasicMaterial vertexColors transparent blending={THREE.AdditiveBlending} depthWrite={false} />
      </lineSegments>
    </group>
  );
};

export default function Orbit() {
  const settings = {
    amount: 15,
    boxSize: 1000,
    speedRatio: 0.05,
    memorySize: 120,
    memoryLineDistance: 350,
    particleSize: 3,
    massiveCenter: true,
    centerMass: 10000
  };

  return (
    <div style={{ width: '100vw', height: '100vh', background: 'black' }}>
      <Canvas camera={{ position: [0, 0, 500], far: 20000 }}>
        <color attach="background" args={['#000']} />
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} />
        <ParticleSystem settings={settings} />
        <Stars radius={2000} depth={50} count={5000} factor={4} saturation={0} fade speed={1} />
        <OrbitControls />
      </Canvas>
    </div>
  );
}